import { BlockNavigation, Button, CharField, Flex, H1, RouterTabs, Tabs, useBreadcrumbs } from "@inmagik/magik-ui"
import Layout from "../../components/Layout"
import { useNavigate, useParams } from "react-router-dom"
import { useEdizione, useEdizioneDelete, useEdizioneUpdate } from "../../hooks/edizioni"
import EdizioneForm from "./EdizioneForm"
import { useMemo, useState } from "react"
import { useEsemplareCreate, useEsemplareUpdate, useEsemplariList } from "../../hooks/esemplari"
import { TableEsemplari } from "../Esemplari/TableEsemplari"
import { Edizione, FiltersBase } from "../../types"
import { useQsFilters } from "../../hooks/filters"
import { Container } from "react-bootstrap"
import useModalTrigger from "magik-react-hooks/useModalTrigger"
import { useFacsimileCreate, useFacsimileUpdate, useFacsimiliList } from "../../hooks/facsimili"
import { TableFacsimili } from "../Facsimili/TableFacsimili"
import { useParatestiList, useParatestoCreate, useParatestoUpdate } from "../../hooks/paratesti"
import { TableParatesti } from "../Paratesti/TableParatesti"
import { Formik } from "formik"
import BottomFormFixed from "../../components/BottomFormFixed"
import EsemplareModal from "../Esemplari/EsemplareModal"
import { initialValuesEdizione, validationSchemaEdizione } from "./EdizioneCommon"
import FacsimileModal from "../Facsimili/FacsimileModal"
import ParatestoModal from "../Paratesti/ParatestoModal"
import { useImmagineCreate, useImmaginePartialUpdate, useImmagineUpdate, useImmagini } from "../../hooks/immagini"
import { TableImmagini } from "../Immagini/TableImmagini"
import ImmagineModal from "../Immagini/ImmagineModal"
import ModalDelete from "../../components/ModalDelete"
import { ScrollLayout } from "../../components/ScrollLayout"
import { useAuth } from "../../auth"
import { transformErrorForForm } from "../../utils/utils"

const initFilters = (params: URLSearchParams) =>
  ({
    page: Number(params.get("page") ?? 1),
    search: params.get("search") ?? "",
    ordering: params.get("ordering") ?? "",
  } as FiltersBase)

export default function EdizioneUpdate() {
  const { filters, uiFilters, setFilters } = useQsFilters(initFilters)
  const { id } = useParams()
  const { data: edizione } = useEdizione(Number(id))

  const updateEdizione = useEdizioneUpdate()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState("dettaglio")

  const { data: esemplari } = useEsemplariList({
    edizione: String(id),
    page: String(filters.page),
    search: filters.search,
    ordering: filters.ordering,
  })
  const { data: facsimili } = useFacsimiliList({
    edizione: String(id),
    page: String(filters.page),
    search: filters.search,
    ordering: filters.ordering,
  })
  const { data: paratestiLegales, refetch: refetchParatestiLegales } = useParatestiList({
    edizione: String(id),
    page: String(filters.page),
    search: filters.search,
    ordering: 'ordine',
    tipologia: "Legales",
  })

  const { data: paratestiSocio, refetch: refetchParatestiSocio } = useParatestiList({
    edizione: String(id),
    page: String(filters.page),
    search: filters.search,
    ordering: 'ordine',
    tipologia: "Socio-literarios",
  })

  const { data: paratestiEditoriales, refetch: refetchParatestiEditoriales } = useParatestiList({
    edizione: String(id),
    page: String(filters.page),
    search: filters.search,
    ordering: 'ordine',
    tipologia: "Editoriales",
  })

  const { data: paratestiTextuales, refetch: refetchParatestiTextuales } = useParatestiList({
    edizione: String(id),
    page: String(filters.page),
    search: filters.search,
    ordering: 'ordine',
    tipologia: "Textuales",
  })

  const { data: immagini } = useImmagini({
    edizione: String(id),
    page: String(filters.page),
    search: filters.search,
    ordering: filters.ordering,
  })

  const createEsemplare = useEsemplareCreate()
  const updateEsemplare = useEsemplareUpdate()

  const createFacsimile = useFacsimileCreate()
  const updateFacsimile = useFacsimileUpdate()

  const createParatesto = useParatestoCreate()
  const updateParatesto = useParatestoUpdate()

  const createImmagine = useImmagineCreate()
  const updateImmagine = useImmaginePartialUpdate()

  const [modalEsemplareCreateState, modalEsemplareCreateActions] = useModalTrigger()
  const [modalEsemplareUpdateState, modalEsemplareUpdateActions] = useModalTrigger()

  const [modalFacsimileCreateState, modalFacsimileCreateActions] = useModalTrigger()
  const [modalFacsimileUpdateState, modalFacsimileUpdateActions] = useModalTrigger()

  const [modalParatestoCreateState, modalParatestoCreateActions] = useModalTrigger()
  const [modalParatestoUpdateState, modalParatestoUpdateActions] = useModalTrigger()

  const [modalImmagineCreateState, modalImmagineCreateActions] = useModalTrigger()
  const [modalImmagineUpdateState, modalImmagineUpdateActions] = useModalTrigger()

  const [modalDeleteEdizioneState, modalDeleteEdizioneActions] = useModalTrigger()

  const deleteEdizione = useEdizioneDelete()

  const initialValues = useMemo(() => initialValuesEdizione(edizione), [edizione])
  const validationSchema = useMemo(() => validationSchemaEdizione(true), [])

  useBreadcrumbs([{ label: "Edizioni", href: "/edizioni/edizioni" }, { label: "Dettaglio edizione" }])

  const { user } = useAuth()

  return (
    <Layout>
      <ScrollLayout hasBottomFixed>
        <Flex alignItems="center" justifyContent="between">
          <H1>{edizione?.titolo_completo || <>&nbsp;</>}</H1>
          {activeTab === "dettaglio" && user?.can_delete_data && (
            <Button
              size="md"
              title="Elimina"
              variant="danger"
              leadingIcon="delete"
              onClick={() => {
                modalDeleteEdizioneActions.open()
              }}
            />
          )}
          {activeTab === "esemplari" && (
            <Button
              size="md"
              className="text-white"
              title="Aggiungi"
              leadingIcon="add"
              onClick={() => {
                modalEsemplareCreateActions.open()
              }}
            />
          )}
          {activeTab === "facsimili" && (
            <Button
              size="md"
              className="text-white"
              title="Aggiungi"
              leadingIcon="add"
              onClick={() => {
                modalFacsimileCreateActions.open()
              }}
            />
          )}
          {(activeTab === "paratesti_legales") && (
            <Button
              size="md"
              className="text-white"
              title="Aggiungi"
              leadingIcon="add"
              onClick={() => {
                modalParatestoCreateActions.open({
                  tipologia: "Legales",
                  ordine: paratestiLegales?.results.length
                })
              }}
            />
          )}
          {(activeTab === "paratesti_socio") && (
            <Button
              size="md"
              className="text-white"
              title="Aggiungi"
              leadingIcon="add"
              onClick={() => {
                modalParatestoCreateActions.open({
                  tipologia: "Socio-literarios",
                  ordine: paratestiSocio?.results.length 
                })
              }}
            />
          )}
          {(activeTab === "paratesti_editoriales") && (
            <Button
              size="md"
              className="text-white"
              title="Aggiungi"
              leadingIcon="add"
              onClick={() => {
                modalParatestoCreateActions.open({
                  tipologia: "Editoriales",
                  ordine: paratestiEditoriales?.results.length
                })
              }}
            />
          )}
          {(activeTab === "paratesti_textuales") && (
            <Button
              size="md"
              className="text-white"
              title="Aggiungi"
              leadingIcon="add"
              onClick={() => {
                modalParatestoCreateActions.open({
                  tipologia: "Textuales",
                  ordine: paratestiTextuales?.results.length
                })
              }}
            />
          )}
          {activeTab === "immagini" && (
            <Button
              size="md"
              className="text-white"
              title="Aggiungi"
              leadingIcon="add"
              onClick={() => {
                modalImmagineCreateActions.open()
              }}
            />
          )}
        </Flex>
        <ScrollLayout.Tabs
          className="mt-6"
          onTabChange={(tab) => {
            setActiveTab(tab)
          }}
        >
          <ScrollLayout.Tab id="dettaglio" label="Dettaglio" key={"dettaglio"}>
            <Formik
              enableReinitialize
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={(values, actions) => {
                return updateEdizione
                  .mutateAsync(values as Edizione)
                  .then((edizione) => {
                    navigate(`/edizioni/edizioni`)
                  })
                  .catch((e) => {
                    actions.setErrors(transformErrorForForm(e))
                  })
              }}
            >
              {({ handleSubmit, dirty, isSubmitting }) => (
                <form onSubmit={handleSubmit}>
                  <Container fluid className="mt-6">
                    <EdizioneForm />
                    <BottomFormFixed url="/edizioni/edizioni" />
                  </Container>
                  {/* <BlockNavigation
                    when={dirty && !isSubmitting}
                    message="Sei sicuro di voler uscire? Le modifiche non salvate andranno perse."
                    title="Attenzione"
                    proceedText="Esci"
                    cancelText="Resta"
                  /> */}
                </form>
              )}
            </Formik>
          </ScrollLayout.Tab>
          <ScrollLayout.Tab id="paratesti_legales" label="Paratesti Legales" key={"paratesti_legales"}>
            <Flex className="mt-3">
              <CharField
                label=""
                placeholder="Cerca..."
                leadingIcon="search"
                style={{
                  width: 300,
                }}
                value={filters.search}
                onChange={(value) => {
                  setFilters({ ...filters, search: value })
                }}
              />
            </Flex>
            <div className="mt-3">
              <TableParatesti
                paratesti={paratestiLegales?.results || []}
                setFilters={setFilters}
                refreshList={refetchParatestiLegales}
                showEdizione={false}
                filters={uiFilters}
                count={paratestiLegales?.count || 0}
                fullCount={paratestiLegales?.full_count || 0}
                actionLink={modalParatestoUpdateActions.open}
              />
            </div>
          </ScrollLayout.Tab>
          <ScrollLayout.Tab id="paratesti_socio" label="Paratesti Socio-literarios" key={"paratesti_socio"}>
            <Flex className="mt-3">
              <CharField
                label=""
                placeholder="Cerca..."
                leadingIcon="search"
                style={{
                  width: 300,
                }}
                value={filters.search}
                onChange={(value) => {
                  setFilters({ ...filters, search: value })
                }}
              />
            </Flex>
            <div className="mt-3">
              <TableParatesti
                paratesti={paratestiSocio?.results || []}
                setFilters={setFilters}
                showEdizione={false}
                filters={uiFilters}
                refreshList={refetchParatestiSocio}
                count={paratestiSocio?.count || 0}
                fullCount={paratestiSocio?.full_count || 0}
                actionLink={modalParatestoUpdateActions.open}
              />
            </div>
          </ScrollLayout.Tab>
          <ScrollLayout.Tab id="paratesti_editoriales" label="Paratesti Editoriales" key={"paratesti_editoriales"}>
            <Flex className="mt-3">
              <CharField
                label=""
                placeholder="Cerca..."
                leadingIcon="search"
                style={{
                  width: 300,
                }}
                value={filters.search}
                onChange={(value) => {
                  setFilters({ ...filters, search: value })
                }}
              />
            </Flex>
            <div className="mt-3">
              <TableParatesti
                paratesti={paratestiEditoriales?.results || []}
                setFilters={setFilters}
                showEdizione={false}
                filters={uiFilters}
                refreshList={refetchParatestiEditoriales}
                count={paratestiEditoriales?.count || 0}
                fullCount={paratestiEditoriales?.full_count || 0}
                actionLink={modalParatestoUpdateActions.open}
              />
            </div>
          </ScrollLayout.Tab>
          <ScrollLayout.Tab id="paratesti_textuales" label="Paratesti Textuales" key={"paratesti_textuales"}>
            <Flex className="mt-3">
              <CharField
                label=""
                placeholder="Cerca..."
                leadingIcon="search"
                style={{
                  width: 300,
                }}
                value={filters.search}
                onChange={(value) => {
                  setFilters({ ...filters, search: value })
                }}
              />
            </Flex>
            <div className="mt-3">
              <TableParatesti
                paratesti={paratestiTextuales?.results || []}
                setFilters={setFilters}
                showEdizione={false}
                filters={uiFilters}
                refreshList={refetchParatestiTextuales}
                count={paratestiTextuales?.count || 0}
                fullCount={paratestiTextuales?.results.length || 0}
                actionLink={modalParatestoUpdateActions.open}
              />
            </div>
          </ScrollLayout.Tab>
          <ScrollLayout.Tab id="facsimili" label="Facsimili" key={"facsimili"}>
            <Flex className="mt-3">
              <CharField
                label=""
                placeholder="Cerca..."
                leadingIcon="search"
                style={{
                  width: 300,
                }}
                value={filters.search}
                onChange={(value) => {
                  setFilters({ ...filters, search: value })
                }}
              />
            </Flex>
            <div className="mt-3">
              <TableFacsimili
                facsimili={facsimili?.results || []}
                setFilters={setFilters}
                showEdizione={false}
                filters={uiFilters}
                count={facsimili?.count || 0}
                fullCount={facsimili?.full_count || 0}
                actionLink={modalFacsimileUpdateActions.open}
              />
            </div>
          </ScrollLayout.Tab>
          <ScrollLayout.Tab id="immagini" label="Immagini" key={"immagini"}>
            <Flex className="mt-3">
              <CharField
                label=""
                placeholder="Cerca..."
                leadingIcon="search"
                style={{
                  width: 300,
                }}
                value={filters.search}
                onChange={(value) => {
                  setFilters({ ...filters, search: value })
                }}
              />
            </Flex>
            <div className="mt-3">
              <TableImmagini
                immagini={immagini?.results || []}
                setFilters={setFilters}
                showEdizione={false}
                filters={uiFilters}
                count={immagini?.count || 0}
                fullCount={immagini?.full_count || 0}
                actionLink={modalImmagineUpdateActions.open}
              />
            </div>
          </ScrollLayout.Tab>
          <ScrollLayout.Tab id="esemplari" label="Esemplari" key={"esemplari"}>
            <Flex className="mt-3">
              <CharField
                label=""
                placeholder="Cerca..."
                leadingIcon="search"
                style={{
                  width: 300,
                }}
                value={filters.search}
                onChange={(value) => {
                  setFilters({ ...filters, search: value })
                }}
              />
            </Flex>
            <div className="mt-3">
              <TableEsemplari
                esemplari={esemplari?.results || []}
                setFilters={setFilters}
                showEdizione={false}
                filters={uiFilters}
                count={esemplari?.count || 0}
                fullCount={esemplari?.full_count || 0}
                actionLink={modalEsemplareUpdateActions.open}
              />
            </div>
          </ScrollLayout.Tab>
        </ScrollLayout.Tabs>
      </ScrollLayout>
      {modalEsemplareUpdateState.isOpen && (
        <EsemplareModal
          show={modalEsemplareUpdateState.isOpen}
          onClose={modalEsemplareUpdateActions.close}
          showEdizione={false}
          esemplare={modalEsemplareUpdateState.value}
          save={(values) => {
            return updateEsemplare
              .mutateAsync({
                ...values,
                edizione: Number(id),
              })
              .then(() => {
                modalEsemplareUpdateActions.close()
              })
          }}
          title="Modifica esemplare"
        />
      )}
      {modalEsemplareCreateState.isOpen && (
        <EsemplareModal
          show={modalEsemplareCreateState.isOpen}
          onClose={modalEsemplareCreateActions.close}
          showEdizione={false}
          save={(values) => {
            return createEsemplare
              .mutateAsync({
                ...values,
                edizione: Number(id),
              })
              .then(() => {
                modalEsemplareCreateActions.close()
              })
          }}
          title="Aggiungi esemplare"
        />
      )}
      {modalFacsimileCreateState.isOpen && (
        <FacsimileModal
          title="Aggiungi facsimile"
          save={(values) => {
            return createFacsimile
              .mutateAsync({
                ...values,
                edizione: Number(id),
              })
              .then(() => {
                modalFacsimileCreateActions.close()
              })
          }}
          show={modalFacsimileCreateState.isOpen}
          onClose={modalFacsimileCreateActions.close}
        />
      )}
      {modalFacsimileUpdateState.isOpen && (
        <FacsimileModal
          title="Modifica facsimile"
          facsimile={modalFacsimileUpdateState.value}
          save={(values) => {
            return updateFacsimile
              .mutateAsync({
                ...values,
                edizione: Number(id),
              })
              .then(() => {
                modalFacsimileUpdateActions.close()
              })
          }}
          show={modalFacsimileUpdateState.isOpen}
          onClose={modalFacsimileUpdateActions.close}
        />
      )}
      {modalParatestoCreateState.isOpen && (
        <ParatestoModal
          title="Aggiungi paratesto"
          defaultTipologia={modalParatestoCreateState.value?.tipologia}
          defaultOrdine={modalParatestoCreateState.value?.ordine}
          save={(values, actions) => {
            return createParatesto
              .mutateAsync({
                ...values,
                edizione: Number(id),
              })
              .then(() => {
                modalParatestoCreateActions.close()
              })
              .catch((e) => {
                actions.setErrors(transformErrorForForm(e))
              })
          }}
          show={modalParatestoCreateState.isOpen}
          onClose={modalParatestoCreateActions.close}
        />
      )}
      {modalParatestoUpdateState.isOpen && (
        <ParatestoModal
          title="Modifica paratesto"
          paratesto={modalParatestoUpdateState.value}
          save={(values, actions) => {
            return updateParatesto
              .mutateAsync({
                ...values,
                edizione: Number(id),
              })
              .then(() => {
                modalParatestoUpdateActions.close()
              })
              .catch((e) => {
                actions.setErrors(transformErrorForForm(e))
              })
          }}
          show={modalParatestoUpdateState.isOpen}
          onClose={modalParatestoUpdateActions.close}
        />
      )}
      {modalImmagineCreateState.isOpen && (
        <ImmagineModal
          title="Aggiungi immagine"
          save={(values, actions) => {
            return createImmagine
              .mutateAsync({
                ...values,
                edizione: Number(id),
              })
              .then(() => {
                modalImmagineCreateActions.close()
              }).catch((e) => {
                actions.setErrors(transformErrorForForm(e))
              })
          }}
          show={modalImmagineCreateState.isOpen}
          onClose={modalImmagineCreateActions.close}
        />
      )}
      {modalImmagineUpdateState.isOpen && (
        <ImmagineModal
          title="Modifica immagine"
          immagine={modalImmagineUpdateState.value}
          save={(values, actions) => {
            const payload = { ...values }
            if (typeof payload.immagine === "string") {
              delete payload.immagine
            }
            return updateImmagine.mutateAsync(payload).then(() => {
              modalImmagineUpdateActions.close()
            }).catch((e) => {
              actions.setErrors(transformErrorForForm(e))
            })
          }}
          show={modalImmagineUpdateState.isOpen}
          onClose={modalImmagineUpdateActions.close}
        />
      )}
      {modalDeleteEdizioneState.isOpen && (
        <ModalDelete
          isOpen={modalDeleteEdizioneState.isOpen}
          onClose={modalDeleteEdizioneActions.close}
          onDelete={() => {
            return deleteEdizione.mutateAsync(Number(id)).then(() => {
              navigate("/edizioni/edizioni")
            })
          }}
          message="Sei sicuro di voler eliminare l'edizione?"
          title="Elimina edizione"
        />
      )}
    </Layout>
  )
}
